"use client";

import { useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, from = "bottom" }: { children: ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const hidden: Record<"bottom" | "left" | "right", string> = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text, light = false }: { text: string; light?: boolean }) {
  return (
    <span className={`block text-[11px] font-bold tracking-[0.15em] uppercase mb-3.5 ${light ? "text-blue-300" : "text-blue-700"}`}>
      {text}
    </span>
  );
}
function Divider({ light = false }) {
  return <div className={`w-11 h-[3px] mb-6 ${light ? "bg-blue-400" : "bg-blue-700"}`} />;
}

/* ─── Data ────────────────────────────────────────────────── */
const ERP_STATS = [
  { v: "30–50%", l: "Reduction in operational costs",    src: "Industry Avg." },
  { v: "10,000+",l: "Users supported without lag",       src: "Platform Spec" },
  { v: "20–30%", l: "Annual ROI post-implementation",    src: "Gartner" },
  { v: "1",      l: "Source of truth across departments",src: "Enterprise Maximiser" },
];

const MODULES = [
  {
    module: "Finance & Accounting",
    caps: "Ledger management, invoicing, GST filing, P&L, balance sheet, cash flow",
  },
  {
    module: "Human Resources",
    caps: "Employee management, payroll, attendance, leave, appraisals, ESS portal",
  },
  {
    module: "Inventory & Warehouse",
    caps: "Stock tracking, multi-warehouse, batch management, expiry tracking, reorder alerts",
  },
  {
    module: "Sales & Order Management",
    caps: "Quote-to-cash, order tracking, customer pricing, delivery management",
  },
  {
    module: "Purchase & Procurement",
    caps: "Vendor management, POs, goods receipt, 3-way matching, approval workflows",
  },
  {
    module: "Production & Manufacturing",
    caps: "BOM, work orders, production planning, quality control, shop floor management",
  },
  {
    module: "CRM Integration",
    caps: "Unified customer data across sales and service with Customer Maximiser",
  },
  {
    module: "Business Intelligence",
    caps: "Custom dashboards, KPI tracking, drill-down reports, board-level analytics",
  },
  {
    module: "Project Management",
    caps: "Task tracking, resource allocation, time logging, project P&L reporting",
  },
  {
    module: "Document Management",
    caps: "Centralized document storage with version control, approval and access management",
  },
];

const WHY_POINTS = [
  "30–50% reduction in operational costs through process automation and elimination of redundancy",
  "Real-time visibility across all departments — one source of truth for your entire organization",
  "Fully customizable — every module, workflow, report and field tailored to your industry",
  "GST-ready with built-in compliance for Indian regulatory requirements",
  "Scales from 10 to 10,000+ users without performance degradation",
  "Industry-specific solutions for manufacturing, retail, services, distribution and education",
];

const FAQS = [
  { q: "What is ERP and why does our business need it?",                      a: "ERP (Enterprise Resource Planning) integrates all core business functions — finance, HR, inventory, sales, procurement, production — into one unified system. It eliminates data silos, reduces manual work and gives leadership real-time visibility across every department. Businesses using ERP typically see 20–30% annual ROI through efficiency gains alone." },
  { q: "How long does an ERP implementation take?",                            a: "A standard ERP implementation takes 6–18 months depending on scope, number of modules, data migration complexity and company size. Enterprise Maximiser's phased approach means you go live with core modules first and expand without disrupting operations." },
  { q: "Is Enterprise Maximiser suitable for Indian businesses and GST compliance?", a: "Yes — Enterprise Maximiser is built with Indian regulatory requirements at its core. GST filing, TDS, e-invoicing (IRN generation) and compliance reporting are all native features, not add-ons. It is also aligned with MCA and ROC filing requirements." },
  { q: "Can the ERP be customized to our industry and workflows?",             a: "Every module, workflow, report, approval chain and data field in Enterprise Maximiser is configurable. We have industry-specific templates for manufacturing, retail, distribution, services and education — and our team builds any additional customizations required during implementation." },
  { q: "What happens to our existing data during migration?",                  a: "Data migration is a dedicated phase in our implementation process. We conduct data audits, cleansing, mapping and validation before any migration. Historical data from your existing systems — accounting, HR, inventory — is migrated with full integrity checks and rollback capability." },
  { q: "What ongoing support is provided post go-live?",                       a: "Post go-live support includes a hypercare period with dedicated on-call consultants, followed by a structured SLA-based support agreement covering bug resolution, configuration changes, user training and module expansions as your business grows." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Module Table Row ────────────────────────────────────── */
function ModuleRow(props: { row: { module: string; caps: string }; i: number }) {
  const { row, i } = props;
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`grid grid-cols-[240px_1fr] border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"} ${i === 0 ? "border-t" : ""}`}
      style={{ transitionDelay: `${i * 0.04}s` }}
    >
      <div className="py-4 px-5 bg-slate-50 border-r border-slate-200 flex items-center">
        <span className="text-[12px] font-bold text-slate-700 tracking-[0.06em] uppercase leading-snug">{row.module}</span>
      </div>
      <div className="py-4 px-5 flex items-center">
        <span className="text-[13.5px] text-slate-500 leading-relaxed">{row.caps}</span>
      </div>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function ERPPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[30px] bg-white">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/it" className="text-blue-700 font-medium hover:underline">IT Solutions</Link>
            <span className="text-slate-300">›</span>
            <span>ERP</span>
          </nav>

          <div className="flex flex-wrap gap-2 mb-5">
            {["Finance", "HR", "Inventory", "Manufacturing", "GST-Ready", "BI"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="IT Solutions — Enterprise Resource Planning" />
          <Divider />

          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-5 ">
            Enterprise Maximiser — One Platform for Your Entire Business
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            Integrate, automate and optimize every business function — finance, HR, inventory, production, sales and more — in one powerful, fully customizable ERP platform built for growth.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              BOOK ERP CONSULTATION
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              FREE NEEDS ASSESSMENT
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <img
            src="/images/services/IT/Banner/ERP.png"
            alt="Enterprise ERP platform"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "10",     l: "ERP Modules" },
          { v: "30–50%", l: "Cost Reduction" },
          { v: "GST",    l: "Ready Out of the Box" },
          { v: "Free",   l: "Needs Assessment" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ WHY ENTERPRISE MAXIMISER ═══════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-900">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <Eyebrow text="Why Enterprise Maximiser" light />
            <Divider light />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-white leading-tight mb-5">
              One System. Every Department. Zero Silos.
            </h2>
            <p className="text-[15px] font-light text-slate-300 leading-relaxed mb-8">
              Most growing businesses run on disconnected spreadsheets, legacy software and manual reconciliation. Enterprise Maximiser replaces all of it — giving every team a single, live source of truth and eliminating the hidden costs of fragmentation.
            </p>
            <div className="flex flex-col gap-4">
              {WHY_POINTS.map((point, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <div className="w-5 h-5 bg-blue-600 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-[14px] text-slate-300 leading-relaxed">{point}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="grid grid-cols-2 gap-0.5">
              {ERP_STATS.map((s, i) => (
                <div key={i} className="bg-slate-800 border border-slate-700 p-6">
                  <p className="text-[32px] font-black text-blue-400 leading-none mb-2">{s.v}</p>
                  <p className="text-[13px] text-slate-300 leading-snug mb-2">{s.l}</p>
                  <p className="text-[11px] font-semibold text-slate-500 tracking-wide uppercase">{s.src}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ ERP MODULES TABLE ══════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="ERP Modules" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Ten Integrated Modules. Complete Business Coverage.
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              Every module is natively integrated — data flows automatically between finance, HR, inventory, sales and production. No manual exports, no reconciliation, no gaps.
            </p>
            <img
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&q=80"
              alt="ERP business intelligence dashboard"
              className="w-full h-[180px] sm:h-[220px] lg:h-[260px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="mt-1">
              {MODULES.map((row, i) => <ModuleRow key={i} row={row} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About ERP
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Straightforward answers to the questions we hear most from businesses evaluating their first ERP or switching from a legacy system.
            </p>
            <img
              src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=700&q=80"
              alt="ERP implementation consultation"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Ready to Transform Your Business?" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Book a Free ERP Consultation
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              We map your current processes, identify the highest-impact modules for your industry and build a phased implementation plan — at no cost, no commitment required.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Most businesses uncover significant hidden costs in their first process audit. The question is whether you find them before they compound further.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                BOOK ERP CONSULTATION
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                FREE NEEDS ASSESSMENT
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}